/**
 * LE DUE CODE — la CLASSIFICATA con orologio e la LIBERA senza orologio — e il
 * CORPO che va sul filo per ciascuna.
 *
 * Vive qui e non dentro `useRankedQueue` per la stessa ragione di
 * `appointments.ts`: **un banco deve poterlo eseguire**. Un gate a
 * `node --experimental-strip-types` non importa un modulo che importa React, e
 * una regex sul sorgente proverebbe che la riga E' SCRITTA, non che produca il
 * corpo giusto.
 *
 * ═══ 🔴 LA DECISIONE DI OGGI (30/09) — e cosa ha superato ═══
 * [Luca, **I-277** del 2026-09-30 ~19:41Z, scelta **L-2606 (a)** «Anello che
 * respira», reference KB `services/qlash-telegram-app/ui/2026-09-30-backgammon-ranked-anello.md`]:
 * *«la coda ranked si attiva solo in modalità ranked with clock … senza clock
 * diventa (per adesso) no ranked»*.
 * ⇒ **La classificata esiste SOLO con l'orologio.** La seconda coda resta, ma
 * non e' piu' una classificata: e' **senza orologio e FUORI CLASSIFICA** —
 * nessun ELO scritto, quindi nessun foglio di conferma (L-782 esiste perche' il
 * tap scrive ELO, e qui l'ELO non si muove).
 * ~~«Due code classificate, con e senza orologio»~~ (L-722 esteso alla TMA il
 * 09/09): superato da I-277 nella meta' «classificata». La meta' sulla DURATA,
 * qui sotto, resta intera.
 *
 * ═══ 🔴 L-722 — NESSUNA DURATA, e questo NON e' cambiato ═══
 * [Luca, `L-722` del 09/09 00:38:25Z]
 * **Due voci d'ingresso, e basta.** ⛔ **Nessuna DURATA**: niente blitz/rapid,
 * niente 180/10 contro 60/5. La durata si concorda **dopo l'abbinamento, dentro
 * l'appuntamento di ADR-0114** — che e' gia' in produzione e **si riusa**. Il
 * criterio per cui la durata tornerebbe ad avere senso e' misurato: **~100
 * persone per coda contro le 6 di oggi.**
 * ⇒ Se in questo file compare un numero di banca o di incremento, qualcuno ha
 * ricostruito la cosa che quella decisione ha tolto.
 *
 * ═══ 🔑 PERCHE' «CON OROLOGIO» E' `{}` E NON `{bankSeconds:180,…}` ═══
 * [M 2026-09-09, `QLASHGeminiZ/QGammon origin/main@3155904`,
 * `matchmaking/routes.ts:46-49`: `timeControl` e' una `z.union` con
 * `.default({bankSeconds:180, incrementSeconds:10})`; conferma indipendente di
 * `matchmaking-orc`, che possiede il disegno della coda.]
 * Mandare `{}` **segue** il default del server; cablare `180/10` lo
 * **inchioda** al valore che era il default oggi. Quando le chiavi per durata
 * verranno fuse, «con orologio» sara' UNA coda e la sua configurazione sara'
 * quella del server: chi manda `{}` ci arriva senza toccare niente, chi ha
 * cablato i numeri si accorge di essere rimasto indietro **solo da un'attesa che
 * non finisce**, che non e' un errore che qualcuno vede.
 * ⇒ ***«Con orologio» significa «quello che dice il server», non «180/10».***
 * E la classificata non manda `rated`: sul server di qgammon-orcr (forma (ii))
 * **`rated` assente = classificata**, quindi `{}` resta il corpo giusto anche
 * dopo il campo nuovo — e resta giusto verso un server che il campo non lo ha.
 *
 * ═══ ⚠️ IL RAMO SENZA OROLOGIO NON E' UN NUMERO PICCOLO ═══
 * [M, stesso ref, `routes.ts:41-43`] E' un ramo della union:
 * `.object({ unlimited: z.literal(true) })`. Il server lo manda su una chiave a
 * se' (`queue.ts:157`, `UNLIMITED_QUEUE_KEY = "mm:q:unlimited"`) ⇒ **bacini
 * veri, non un filtro dentro la stessa coda.** ⛔ Nessun `bankSeconds: 0`: due
 * zeri si leggono «tempo scaduto», che e' il contrario di quello che e'
 * successo, e il server infatti li rifiuta (minimo 30).
 *
 * ═══ 🔴 E `rated: false` NON SI CREDE: SI RILEGGE ═══
 * Il campo `rated` e' di qgammon-orcr (pezzo Q del fronte `ranked-bg`):
 * opzionale su `POST /matchmaking/queue`, assente = `true`, con una chiave di
 * coda propria e l'ECO nella risposta. Lo schema del server e' uno `z.object`
 * NON stretto [M QGammon `origin/main@8907279`, `matchmaking/routes.ts:46`]:
 * **un server che il campo non lo conosce lo SCARTA e risponde `200`** — e
 * mette il giocatore nella coda senza orologio CLASSIFICATA, mentre la UI dice
 * «fuori classifica». Nessun errore lo direbbe: arriverebbe un ELO scritto.
 * ⇒ Per la coda libera l'eco deve dire **`rated === false`, esplicitamente**:
 * assente o `true` e' la coda sbagliata (fail-closed). E' l'OPPOSTO della
 * regola sull'orologio, dove il silenzio non e' un disaccordo — e la ragione e'
 * il verso del danno: tacere sull'orologio lascia una ricerca sana, tacere su
 * `rated` lascia un ELO scritto a chi aveva scelto di non rischiarlo.
 * La pill che porta a questa coda sta dietro una serratura
 * (`env.featureBackgammonUnrankedQueueEnabled`) finche' Q non e' su
 * qgammon-stg: senza eco, questa regola farebbe fallire OGNI ingresso.
 */

/**
 * Quale delle due code. Due valori, e non ce ne sara' un terzo per la durata.
 * ⚠️ `unlimited` NON e' piu' «classificata senza orologio» (I-277, 30/09): e' la
 * coda senza orologio **fuori classifica**. Il nome resta quello dell'asse che
 * si vede sul filo (`timeControl.unlimited`).
 */
export type RankedQueueMode = 'clocked' | 'unlimited';

/**
 * Il corpo esatto di `POST /matchmaking/queue` per un modo.
 *
 * ⛔ `clocked` ⇒ **oggetto vuoto**, non i numeri e non `rated`: v. il blocco in
 * testa. Il server applica il suo default (orologio, classificata) e questo
 * client lo segue.
 * ⛔ `unlimited` ⇒ il ramo della union **e** `rated: false`, entrambi LETTERALI:
 * un `"false"` stringa, o un `unlimited: "si"`, cadrebbero in silenzio sul ramo
 * sbagliato (v. `tools/verify-coda.ts`).
 */
export function queueBody(mode: RankedQueueMode): Record<string, unknown> {
  return mode === 'unlimited' ? { timeControl: { unlimited: true }, rated: false } : {};
}

/**
 * La chiave di copy per una voce d'ingresso.
 *
 * ⛔ **Non si adottano i nomi di una superficie sola.** `next` chiama «Blitz» un
 * 180/10 che nel server si chiama `rapid`, e il server ha un `blitz` (60/5) che
 * su `next` non ha bottone: **due parole uguali su cose diverse non danno
 * nessun errore.** Sul filo passano i numeri; qui passano due nomi che
 * descrivono la SCELTA e non un preset: «Classificata · con orologio» e, dal
 * 30/09, «Senza orologio · fuori classifica» (non piu' «Classificata · senza
 * orologio», che non esiste piu').
 */
export function queueLabelKey(mode: RankedQueueMode): string {
  return mode === 'unlimited' ? 'ranked.unranked' : 'ranked.clocked';
}

/**
 * QUALE OROLOGIO DICE IL SERVER, letto dal `timeControl` che rimanda indietro —
 * `null` se non lo dice.
 *
 * ═══ 🔴 PERCHE' SERVE: IL SERVER NON VALIDA IL PAYLOAD ═══
 * [M `or43` 2026-09-09, staging, token vero, `POST /matchmaking/queue`]:
 *  · `{"timeControl":{"unlimited":true}}`      → **200**, echo `unlimited:true`;
 *  · `{"timeControl":{"campoInventato":true}}` → **200**, echo `{bankSeconds:180,incrementSeconds:10}`;
 *  · `{"timeControl":{"unlimited":"si"}}`      → **200**, stesso default.
 * ⇒ ***Un corpo sbagliato non prende un `400`: prende un `200` e la coda
 * 180/10.*** Il client finisce in un bacino che non ha chiesto, **non si
 * accoppia con nessuno di quelli che voleva, e nessun errore glielo dice** —
 * con sei giocatori e' indistinguibile da «non c'e' nessuno in coda».
 * 🔑 ⇒ **Il codice HTTP non e' il discriminante.** L'unico modo di accorgersene
 * e' **rileggere cio' che il server RIMANDA e confrontarlo con cio' che si e'
 * chiesto** — la stessa disciplina del ritiro (#210): *il rifiuto arriva muto,
 * quindi si rilegge sempre.*
 *
 * ⛔ **SI CONFRONTA L'ASSE, NON I NUMERI.** «Con orologio» significa «quello
 * che dice il server» (si manda `{}`): pretendere `180/10` nella risposta
 * rifarebbe l'inchiodatura che `queueBody` esiste per evitare, e andrebbe rosso
 * il giorno che il default cambia — cioe' proprio quando tutto funziona.
 * ⚠️ E `null` significa **«non l'ha detto»**, mai «e' sbagliato»: un server piu'
 * vecchio dell'echo non deve poter produrre un allarme SULL'OROLOGIO. (Su
 * `rated` la regola e' opposta: v. `queueMismatch`.)
 * 📌 Lo legge anche la Live (`partiteLive.ts`): li' dice solo «con/senza
 * orologio», che resta vero.
 */
export function modeOfTimeControl(timeControl: unknown): RankedQueueMode | null {
  if (!timeControl || typeof timeControl !== 'object') return null;
  const tc = timeControl as { unlimited?: unknown; bankSeconds?: unknown };
  if (tc.unlimited === true) return 'unlimited';
  if (typeof tc.bankSeconds === 'number') return 'clocked';
  return null;
}

/**
 * Cio' che il server rimanda dopo un ingresso in coda (corpo del `200`, o la
 * `GET /matchmaking/queue` sul ramo `409 already_queued`). Tutto `unknown`: e'
 * un altro servizio, e un campo che non c'e' e' un'informazione, non un errore.
 */
export interface QueueEcho {
  timeControl?: unknown;
  rated?: unknown;
}

/**
 * QUALE ASSE discorda fra cio' che si e' chiesto e cio' che il server rimanda —
 * `null` se nessuno.
 *
 *  · **`clock`**: il server ha DETTO un orologio diverso da quello chiesto. Solo
 *    su un disaccordo dichiarato: il silenzio sull'orologio non ferma niente.
 *  · **`rated`**, per la coda LIBERA: l'eco non dice `rated === false`. Assente
 *    o `true` e' la stessa cosa — «non confermata fuori classifica» — perche' un
 *    server che non conosce il campo mette in classificata (v. il blocco in
 *    testa). ⛔ Fail-closed, ed e' voluto.
 *  · **`rated`**, per la CLASSIFICATA: solo un `rated === false` DICHIARATO (il
 *    giocatore ha confermato una partita che scrive ELO, e gli si darebbe una
 *    partita che non lo scrive). Assente o `true` va bene: assente = classificata.
 *
 * L'orologio si guarda PRIMA: e' l'asse che sa dire in quale coda si e' finiti,
 * e l'avviso che ne nasce la nomina.
 */
export function queueMismatch(
  asked: RankedQueueMode,
  echo: QueueEcho | null,
): 'clock' | 'rated' | null {
  const got = modeOfTimeControl(echo?.timeControl);
  if (got !== null && got !== asked) return 'clock';
  const rated = echo?.rated;
  if (asked === 'unlimited') return rated === false ? null : 'rated';
  return rated === false ? 'rated' : null;
}

/**
 * Sono finito nella coda sbagliata? Vero su un orologio che discorda (solo se
 * dichiarato) **o** su una coda libera che il server non conferma fuori
 * classifica. V. `queueMismatch`.
 *
 * ⛔ Il silenzio del server SULL'OROLOGIO non e' un disaccordo, ed e' la meta'
 * che conta per la classificata: una guardia che scattasse su «non lo so»
 * fermerebbe la ricerca contro un server perfettamente sano che semplicemente
 * non echeggia.
 */
export function wrongQueue(asked: RankedQueueMode, echo: QueueEcho | null): boolean {
  return queueMismatch(asked, echo) !== null;
}
